import { Text } from '@/components/text';
import { Tooltip } from '@/components/ui/tooltip';
import { Colors } from '@/constants/theme';
import type { Country } from '@/types/country';
import MaterialIcons from '@expo/vector-icons/MaterialIcons';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    BlurEvent,
    Pressable,
    ScrollView,
    StyleSheet,
    TextInput,
    View
} from 'react-native';
import { useCountryName } from '@/hooks/useCountryName';
import { Chip } from './ui/chip';
import { PortalDropdown } from './ui/portal-dropdown';

type Props = {
    label: string;
    tooltip?: string;
    countries: Country[];
    valueIds: string[];
    onChange: (ids: string[]) => void;
    placeholder?: string;
    disabled?: boolean;
    excludeIds?: string[];
    disabledIds?: string[];
    maxResults?: number;
    id?: string;
    name?: string;
};

function normalize(s: string) {
    return s.trim().toLowerCase();
}

export function MultiCountrySelect({
    label,
    tooltip,
    countries,
    valueIds = [],
    onChange,
    placeholder,
    disabled,
    excludeIds,
    disabledIds,
    maxResults = 500,
    id,
    name,
}: Props) {
    const { t } = useTranslation();
    const getLocalizedName = useCountryName();

    const selectedCountries = useMemo(
        () => countries.filter(c => valueIds.includes(c.id)),
        [countries, valueIds]
    );

    const selectedIds = useMemo(() => new Set(valueIds), [valueIds]);

    const [open, setOpen] = useState(false);
    const [q, setQ] = useState('');

    const pointerDownInsideRef = useRef(false);
    const containerRef = useRef<View>(null);
    const anchorRef = useRef<View>(null);
    const inputRef = useRef<TextInput>(null);

    // Combine external excludes with currently selected ones (for the dropdown list)
    const excludedSet = useMemo(() => {
        const s = new Set(excludeIds ?? []);
        // We usually want to exclude selected items from the dropdown in a multi-select
        valueIds.forEach(id => s.add(id));
        return s;
    }, [excludeIds, valueIds]);

    const disabledSet = useMemo(() => new Set(disabledIds ?? []), [disabledIds]);

    const filtered = useMemo(() => {
        const query = normalize(q);
        const base = countries.filter(c => !excludedSet.has(c.id));

        if (!query) return base.slice(0, maxResults);

        const out: Country[] = [];
        for (const c of base) {
            const name = getLocalizedName(c).toLowerCase();
            const id = c.id.toLowerCase();
            const cur = (c.currencyISO ?? '').toLowerCase();
            if (name.includes(query) || id.includes(query) || cur.includes(query)) out.push(c);
            if (out.length >= maxResults) break;
        }
        return out;
    }, [countries, excludedSet, q, maxResults, getLocalizedName]);

    function openDropdown() {
        if (disabled) return;
        setOpen(true);
        setQ('');
    }

    function closeDropdown() {
        setOpen(false);
        setQ('');
        pointerDownInsideRef.current = false;
    }

    function onBlurInput(_e: BlurEvent) {
        // If we click on a dropdown item, we don't want to close immediately before the onPress fires
        setTimeout(() => {
            if (pointerDownInsideRef.current || inputRef.current?.isFocused()) return;
            closeDropdown();
        }, 150);
    }

    const removeId = (id: string) => {
        onChange(valueIds.filter(v => v !== id));
    };

    const addId = (id: string) => {
        onChange([...valueIds, id]);
        setQ('');
        // Keep focus on input to allow continuous selection?
        inputRef.current?.focus();
    };

    const toggleCountry = (country: Country) => {
        if (selectedIds.has(country.id)) {
            removeId(country.id);
        } else {
            addId(country.id);
        }
    };

    return (
        <View ref={containerRef} style={styles.container}>
            <View style={styles.labelRow}>
                <Text style={{ color: Colors.light.primary }}>{label}</Text>
                {tooltip && <Tooltip content={tooltip} >
                    <MaterialIcons name="info" size={18} color={Colors.light.primary} />
                </Tooltip>}
            </View>

            <View style={styles.anchor} ref={anchorRef}>
                <Pressable
                    style={[styles.inputWrap, disabled && styles.disabledOpacity, open && styles.inputWrapActive]}
                    onPress={() => {
                        if (!disabled) inputRef.current?.focus();
                    }}>
                    <View style={styles.chipsContainer}>
                        {selectedCountries.map(c => (
                            <Chip
                                key={c.id}
                                label={getLocalizedName(c)}
                                disabled={disabled}
                                onRemove={() => removeId(c.id)}
                            />
                        ))}
                        <TextInput
                            ref={inputRef}
                            nativeID={id}
                            {...({ name } as any)}
                            value={q}
                            editable={!disabled}
                            placeholder={
                                selectedCountries.length === 0 ? placeholder ?? t('common.search', 'Search...') : ''
                            }
                            placeholderTextColor={Colors.light.placeholderText}
                            onFocus={openDropdown}
                            onBlur={onBlurInput}
                            onChangeText={txt => {
                                if (!open) setOpen(true);
                                setQ(txt);
                            }}
                            style={styles.input}
                        />
                    </View>

                    <View style={styles.actions}>
                        {valueIds.length > 0 && !disabled && (
                            <Pressable
                                onPress={e => {
                                    e.stopPropagation();
                                    onChange([]);
                                    if (open) setQ('');
                                }}
                                hitSlop={8}
                                style={styles.iconButton}
                                accessibilityLabel={t('common.clear', 'Clear')}>
                                <MaterialIcons name="close" size={18} color="rgba(0,0,0,0.45)" />
                            </Pressable>
                        )}

                        <Pressable
                            onPress={e => {
                                e.stopPropagation();
                                open ? closeDropdown() : openDropdown();
                            }}
                            hitSlop={8}
                            style={styles.iconButton}
                            accessibilityLabel={t('common.toggle', 'Toggle')}>
                            <MaterialIcons
                                name={open ? 'keyboard-arrow-up' : 'keyboard-arrow-down'}
                                size={22}
                                color="rgba(0,0,0,0.55)"
                            />
                        </Pressable>
                    </View>
                </Pressable>

                <PortalDropdown
                    open={open && !disabled}
                    onClose={() => closeDropdown()}
                    anchorRef={anchorRef}
                    outsideClickRef={containerRef}
                    maxHeight={260}
                >
                    <ScrollView
                        keyboardShouldPersistTaps="handled"
                        contentContainerStyle={styles.listContent}
                    >
                        {filtered.length === 0 ? (
                            <View style={styles.empty}>
                                <Text>{t('common.noMatches', 'No matches')}</Text>
                            </View>
                        ) : (
                            filtered.map((item, index) => {
                                const isSelected = selectedIds.has(item.id);
                                const isDisabled = disabledSet.has(item.id);

                                const content = (
                                    <Pressable
                                        onPress={() => {
                                            if (isDisabled) return;
                                            toggleCountry(item);
                                        }}
                                        style={[
                                            styles.option,
                                            isDisabled && styles.optionDisabled,
                                            isSelected && styles.optionSelected,
                                        ]}
                                    >
                                        <View style={styles.optionContent}>
                                            <Text
                                                type={isSelected ? 'defaultSemiBold' : 'default'}
                                                style={[styles.optionText, isSelected && styles.optionTextSelected, isDisabled && styles.optionTextDisabled]}>
                                                {getLocalizedName(item)}
                                            </Text>
                                            {isDisabled && (
                                                <MaterialIcons name="info" color={Colors.light.error} size={20} />
                                            )}
                                        </View>

                                        {isSelected ? (
                                            <MaterialIcons name="check" size={18} color={Colors.light.primary} />
                                        ) : null}
                                    </Pressable>
                                );

                                if (isDisabled) {
                                    const side = index < 2 ? 'bottom' : 'top';
                                    return (
                                        <Tooltip key={item.id} content={t('onboarding.warnings.exchangeControl')} fullWidth side={side}>
                                            {content}
                                        </Tooltip>
                                    );
                                }

                                return <View key={item.id}>{content}</View>;
                            })
                        )}
                    </ScrollView>
                </PortalDropdown>
            </View>
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        gap: 10,
    },
    labelRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,

    },
    anchor: {
        position: 'relative',
        zIndex: 1000,
    },
    inputWrap: {
        flexDirection: 'row',
        alignItems: 'center',
        minHeight: 46,
        borderWidth: 1,
        borderColor: '#898b8c',
        borderRadius: 12,
        paddingHorizontal: 12,
        paddingVertical: 8,
        backgroundColor: '#fff',
    },
    inputWrapActive: {
        borderColor: Colors.light.primary,
    },
    disabledOpacity: {
        opacity: 0.6,
    },
    chipsContainer: {
        flex: 1,
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 6,
        alignItems: 'center',
    },
    input: {
        flex: 1,
        minWidth: 60,
        outlineStyle: 'none' as any,
        fontSize: 15,
        paddingVertical: 0,
    },
    actions: {
        flexDirection: 'row',
        alignItems: 'center',
        alignSelf: 'center', // Center vertically in wrapper
        gap: 2,
        marginLeft: 8,
    },
    iconButton: {
        padding: 4,
    },
    listContent: {
        alignItems: 'stretch',
    },
    option: {
        paddingHorizontal: 12,
        paddingVertical: 10,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        width: '100%',
    },
    optionContent: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        flex: 1,
    },
    optionDisabled: {
        backgroundColor: 'rgba(0,0,0,0.02)',
        cursor: 'not-allowed' as any,
    },
    optionSelected: {
        backgroundColor: 'rgba(48, 87, 133, 0.05)',
    },
    optionTextDisabled: {
        color: 'rgba(0,0,0,0.45)',
    },
    optionText: {
        fontSize: 15,
    },
    optionTextSelected: {
        color: Colors.light.primary,
    },
    empty: { padding: 12 },
});
